import { useCallback } from 'react';
import { deleteAllocationById, deleteAllocationsBulk, updateAllocationFields } from "@/services/portfolio/PortfolioAllocationsService";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/portfolioCalculations";
import { schedulePortfolioStatusRecompute } from "@/utils/portfolioStatusUpdater";

export const useAllocationManagement = (onSuccess: () => void, currencyCode?: string, portfolioId?: string) => {
  const handleDeleteAllocation = useCallback(async (allocationId: string) => {
    try {
      await deleteAllocationById(allocationId);
      // Schedule debounced status recompute now that an allocation was removed
      try { if (portfolioId) schedulePortfolioStatusRecompute(portfolioId); } catch {}
      // Quiet success: no toast
      onSuccess();
    } catch (error) {
      console.error('[Delete Allocation] Failed:', error);
      toast.error("Failed to remove asset allocation");
    }
  }, [onSuccess]);

  const handleUpdateAllocation = useCallback(async (allocationId: string, newPercentage: number) => {
    try {
      // Allow zero allocations for clearing
      const clampedPercentage = Math.max(0, Math.min(100, Math.round(newPercentage * 100) / 100));
      
      // Quiet info: no toast on clamping within valid range

      await updateAllocationFields(allocationId, {
        allocation_percentage: clampedPercentage,
        // When percentage is edited directly, unlock dollar value.
        // IMPORTANT: Do NOT send allocation_value here. The DB trigger
        // "sync_portfolio_allocation" treats changes to allocation_value as
        // the source of truth and will recompute percentage from it (null -> 0).
        // This previously caused allocations to zero unexpectedly.
        is_value_locked: false as any,
        updated_at: new Date().toISOString()
      } as any);
      // Schedule debounced status recompute after percentage edit
      try { if (portfolioId) schedulePortfolioStatusRecompute(portfolioId); } catch {}
      onSuccess();
    } catch (error) {
      console.error('[Update Allocation] Failed:', error);
      toast.error("Failed to update allocation");
    }
  }, [onSuccess]);

  const handleUpdateAllocationByValue = useCallback(async (
    allocationId: string, 
    dollarValue: number, 
    portfolioSize: number,
    isPortfolioSizeLocked: boolean = true,
    onPortfolioSizeChange?: (newSize: number) => Promise<boolean>
  ) => {
    try {
      if (dollarValue < 0) {
        toast.error("Dollar amount must be positive");
        return;
      }
      
      if (portfolioSize <= 0) {
        toast.error("Portfolio size must be greater than zero");
        return;
      }

      let newPercentage = (dollarValue / portfolioSize) * 100;
      let finalPortfolioSize = portfolioSize;
      
      // Debug logging to track the lock state issue
      console.debug('[AllocationManagement] Lock state check:', {
        isPortfolioSizeLocked,
        dollarValue,
        portfolioSize,
        newPercentage,
        wouldExceed: newPercentage > 100
      });
      
      // If portfolio size is locked, validate percentage bounds
      if (isPortfolioSizeLocked) {
        if (newPercentage > 100) {
          const maxAllowed = portfolioSize; // Max dollar amount for 100%
          const msg = formatCurrency(maxAllowed, currencyCode);
          toast.error(`Amount exceeds portfolio size. Maximum allowed: ${msg}`);
          return;
        }
      } else {
        // AUTO-GROWTH (conditional): If unlocked and this allocation exceeds 100%,
        // only auto-expand immediately if the user has already chosen Auto-size in this session.
        if (newPercentage > 100) {
          const hasSeen = (() => {
            try { return sessionStorage.getItem('portfolio-overflow-seen') === 'true'; } catch { return false; }
          })();
          const prefersAuto = (() => {
            try { return sessionStorage.getItem('portfolio-overflow-preference') === 'auto-size'; } catch { return false; }
          })();

          console.debug('[AllocationManagement] Overflow on dollar edit', {
            portfolioSize,
            dollarValue,
            newPercentage,
            hasSeenOverflowDialog: hasSeen,
            prefersAutoSize: prefersAuto
          });

          // Record the user's intended oversize edit so we can persist it AFTER the portfolio size expands
          try {
            sessionStorage.setItem(
              'portfolio-pending-oversize',
              JSON.stringify({ allocationId, dollarValue, expires: Date.now() + 8000 })
            );
          } catch {}

          if (hasSeen && prefersAuto) {
            console.debug('[AllocationManagement] Auto-expanding (session preference) from', portfolioSize, 'to', dollarValue);
            finalPortfolioSize = dollarValue; // Set portfolio size to match this allocation
            // Mark autosizing in progress to suppress dialogs/duplicate actions
            try { sessionStorage.setItem('portfolio-overflow-autosizing', String(Date.now() + 4000)); } catch {}
            if (onPortfolioSizeChange) {
              const success = await onPortfolioSizeChange(finalPortfolioSize);
              if (!success) {
                toast.error("Failed to update portfolio size");
                return;
              }
              // Quiet info: no toast on auto-expansion
            } else {
              console.warn('[AllocationManagement] No onPortfolioSizeChange callback provided for auto-expansion');
            }
            // IMPORTANT: Do not persist the allocation row yet to avoid timing races with the size update.
            // A follow-up commit will occur after the size update completes.
            return;
          } else {
            // Do not auto-expand yet; allow totals to overflow so TotalsSection can present the dialog.
            // Keep finalPortfolioSize unchanged. Avoid persisting this oversize value now to prevent DB violations.
            try {
              // Force the overflow dialog to open on next TotalsSection effect with a short TTL
              sessionStorage.setItem('portfolio-overflow-force', String(Date.now() + 3000));
            } catch {}
            return;
          }
        }
      }

      // Compute raw percentage (before clamping) for flow control
      const rawPct = newPercentage;
      // Clamp and round percentage to prevent constraint violations
      newPercentage = Math.max(0, Math.min(100, Math.round(newPercentage * 100) / 100));

      // Safety guard: if autosizing is in progress, or a pending oversize exists for this allocation AND the raw percentage still exceeds 100,
      // skip persisting now. Allow persist after size update when rawPct <= 100 even if pending flag is present.
      try {
        const autosizingUntil = Number(sessionStorage.getItem('portfolio-overflow-autosizing') || '0');
        const pendingRaw = sessionStorage.getItem('portfolio-pending-oversize');
        const pending = pendingRaw ? JSON.parse(pendingRaw) : null;
        const pendingForThis = pending && pending.allocationId === allocationId && pending.expires > Date.now();
        const autosizingActive = !!(autosizingUntil && Date.now() < autosizingUntil);
        // Only skip if still oversize (>100%) and either autosizing is active or this allocation is pending
        if (rawPct > 100 && (autosizingActive || pendingForThis)) {
          console.debug('[AllocationManagement] Skipping immediate persist during autosize/pending flow', {
            autosizingActive,
            pendingForThis,
            rawPct
          });
          return;
        }
      } catch {}

      await updateAllocationFields(allocationId, {
        allocation_value: Math.round(dollarValue * 100) / 100,
        is_value_locked: true as any,
        updated_at: new Date().toISOString()
      } as any);
      // Schedule debounced status recompute after dollar edit
      try { if (portfolioId) schedulePortfolioStatusRecompute(portfolioId); } catch {}
      onSuccess();
    } catch (error) {
      // Suppress noisy toasts if this is the expected constraint during autosize/pending flows
      const maybe = error as any;
      const code = maybe?.code || maybe?.message;
      let suppressToast = false;
      try {
        const autosizingUntil = Number(sessionStorage.getItem('portfolio-overflow-autosizing') || '0');
        const pendingRaw = sessionStorage.getItem('portfolio-pending-oversize');
        const pending = pendingRaw ? JSON.parse(pendingRaw) : null;
        const pendingActive = pending && pending.expires > Date.now();
        if ((autosizingUntil && Date.now() < autosizingUntil) || pendingActive) {
          suppressToast = true;
        }
      } catch {}
      if (code === '23514' || String(code).includes('allocation_percentage_check')) {
        // Expected if a stray persist slipped in before size update landed
        console.warn('[Update Allocation By Value] Constraint during autosize - suppressing toast:', maybe);
        // Quiet info: no toast here; UI will reconcile after size update
        return;
      }
      console.error('[Update Allocation By Value] Failed:', error);
      if (!suppressToast) toast.error("Failed to update allocation dollar value");
    }
  }, [onSuccess]);

  const handleBulkDelete = useCallback(async (ids: string[]) => {
    try {
      if (!ids || ids.length === 0) {
        toast.error("No allocations selected");
        return;
      }
      await deleteAllocationsBulk(ids);
      // Schedule debounced status recompute after bulk delete
      try { if (portfolioId) schedulePortfolioStatusRecompute(portfolioId); } catch {}
      // Quiet success: no toast
      onSuccess();
    } catch (error) {
      console.error('[Bulk Delete Allocations] Failed:', error);
      toast.error("Failed to remove selected allocations");
    }
  }, [onSuccess]);

  return {
    handleDeleteAllocation,
    handleUpdateAllocation,
    handleUpdateAllocationByValue,
    handleBulkDelete,
  };
};
